import React from "react";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { graphql } from "relay-runtime";

import { RunQuery } from "~/lib/relayUtil";
import Tippy from "~/ui/Tooltip";

import { CycleSelector } from "./CycleSelector";
import { ProjectSelector } from "./ProjectSelector";
import { StateSelector } from "./StateSelector";
import { TeamSelector } from "./TeamSelector";

import type { LinearAllColumnsTeamInfoQuery as TeamInfoQuery } from "~/__generated__/LinearAllColumnsTeamInfoQuery.graphql";

import styles from "~/integrations/linear/css/styles.module.css";

export type TeamInfo = NonNullable<
	NonNullable<TeamInfoQuery["response"]["viewer"]["integrations"]["linear"]>["teamInfo"]
>;

const teamInfoQuery = graphql`
	query LinearAllColumnsTeamInfoQuery($teamId: String!) {
		viewer {
			integrations {
				linear {
					teamInfo(teamId: $teamId) {
						defaultStateId

						states {
							name
							type
							externalId
						}

						projects {
							name
							externalId
						}

						cycles {
							name
							externalId
							number
							startsAt
							endsAt
						}
					}
				}
			}
		}
	}
`;

export function LinearAllColumns({
	teamId,
	setTeamId,
	projectId,
	setProjectId,
	cycleId,
	setCycleId,
	stateId,
	setStateId,
	uncheckedStateId,
	setUncheckedStateId,
	doneStateId,
	setDoneStateId,
}: {
	teamId?: string;
	setTeamId: (v: string) => void;
	projectId?: string;
	setProjectId: (v?: string) => void;
	cycleId?: string;
	setCycleId: (v?: string) => void;
	stateId?: string;
	setStateId: (v?: string) => void;
	uncheckedStateId?: string;
	setUncheckedStateId: (v?: string) => void;
	doneStateId?: string;
	setDoneStateId: (v?: string) => void;
}) {
	const [teamInfo, setTeamInfo] = React.useState<TeamInfo | undefined>();
	const [hasDuplicatedStatuses, setHasDuplicatedStatuses] = React.useState(false);

	const onTeamInfoResponse = React.useCallback(
		(response: TeamInfoQuery["response"]) => {
			if (!response.viewer.integrations.linear?.teamInfo) {
				return;
			}

			const info = response.viewer.integrations.linear.teamInfo;
			const defaultState = info.states?.find(state => state.externalId === info.defaultStateId);

			setProjectId(undefined);
			setCycleId(undefined);
			setStateId(defaultState ? defaultState.externalId : undefined);
			setTeamInfo(info);
		},
		[setProjectId, setCycleId, setStateId],
	);

	const onTeamInfoLoading = React.useCallback(() => {
		setTeamInfo(undefined);
	}, []);

	React.useEffect(() => {
		setHasDuplicatedStatuses(!!uncheckedStateId && !!doneStateId && uncheckedStateId === doneStateId);
	}, [uncheckedStateId, doneStateId]);

	return (
		<>
			<div className={styles.flexRow}>
				<div className={styles.column}>
					<div className={styles.label}>Team</div>
					<TeamSelector teamId={teamId} setTeamId={setTeamId} />
				</div>

				{teamId ? (
					<RunQuery<TeamInfoQuery>
						query={teamInfoQuery}
						variables={{ teamId }}
						onResult={onTeamInfoResponse}
						onLoading={onTeamInfoLoading}
					/>
				) : null}

				<div className={styles.column}>
					<div className={styles.label}>Project</div>
					<ProjectSelector
						teamId={teamId}
						projectId={projectId}
						setProjectId={setProjectId}
						projects={teamInfo?.projects}
					/>
				</div>
			</div>

			<div className={styles.flexRow}>
				<div className={styles.column}>
					<div className={styles.label}>Cycle</div>
					<CycleSelector teamId={teamId} cycleId={cycleId} setCycleId={setCycleId} cycles={teamInfo?.cycles} />
				</div>

				<div className={styles.column}>
					<div className={styles.label}>
						Status
						<Tippy
							placement="top"
							className={styles.toolTip}
							content={"When you create this item, the related Linear issue will have this value as the initial status"}
						>
							<span className={styles.helpPopup}>
								<FontAwesomeIcon icon={faInfoCircle} />
							</span>
						</Tippy>
					</div>
					<StateSelector teamId={teamId} stateId={stateId} setStateId={setStateId} states={teamInfo?.states} />
				</div>
			</div>

			<div className={styles.flexRow}>
				<div className={styles.column}>
					<div className={styles.label}>
						Unchecked status
						<Tippy
							placement="top"
							className={styles.toolTip}
							content={"When you uncheck this item, select the status that will be set in Linear"}
						>
							<span className={styles.helpPopup}>
								<FontAwesomeIcon icon={faInfoCircle} />
							</span>
						</Tippy>
					</div>
					<StateSelector
						teamId={teamId}
						stateId={uncheckedStateId}
						setStateId={setUncheckedStateId}
						states={teamInfo?.states}
						isError={hasDuplicatedStatuses}
					/>
				</div>

				<div className={styles.column}>
					<div className={styles.label}>
						Done status
						<Tippy
							placement="top"
							className={styles.toolTip}
							content={"When you check this item, select the status that will be set in Linear"}
						>
							<span className={styles.helpPopup}>
								<FontAwesomeIcon icon={faInfoCircle} />
							</span>
						</Tippy>
					</div>
					<StateSelector
						teamId={teamId}
						stateId={doneStateId}
						setStateId={setDoneStateId}
						states={teamInfo?.states}
						isError={hasDuplicatedStatuses}
					/>
				</div>
			</div>
		</>
	);
}
